import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { router } from "@platform/router";
import { I18nProvider } from "@platform/core/i18n/context";
import { queryClient } from "@platform/core/query";

const THEMES = ["light", "dark", "system"];

// next-themes paints whatever localStorage holds, so a reader carrying a retired theme has to be
// rewritten before it mounts. Every name that shipped ended in "dark" exactly when it was dark.
function dropRetiredTheme() {
	try {
		const stored = localStorage.getItem("platform-theme");
		if (stored && !THEMES.includes(stored)) {
			localStorage.setItem("platform-theme", stored.endsWith("dark") ? "dark" : "light");
		}
	} catch {
		// A blocked storage leaves next-themes on its default, which is already a live theme.
	}
}

// AuthProvider is not here. It mounts inside the authenticated shell, so its
// session and member reads never fire on public routes.
const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

dropRetiredTheme();

createRoot(root).render(
	<StrictMode>
		<ThemeProvider
			attribute="class"
			defaultTheme="light"
			themes={THEMES}
			storageKey="platform-theme"
			disableTransitionOnChange
		>
			<I18nProvider>
				<QueryClientProvider client={queryClient}>
					<RouterProvider router={router} />
				</QueryClientProvider>
			</I18nProvider>
		</ThemeProvider>
	</StrictMode>,
);
